import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { CardSurface } from "@/components/card/CardSurface";
import { PriceSummary } from "@/components/PriceSummary";
import { useCatalog } from "@/hooks/useCatalog";
import { useQuote } from "@/hooks/useQuote";
import { useOrder } from "@/state/OrderContext";
import { createOrder, initPayment } from "@/lib/api";
import { formatCardAddress, formatQty, formatShippingAddress } from "@/lib/formatters";
import { LOGO_CHOICES } from "@/lib/constants";

const logoLabel = (choice) =>
  ({
    [LOGO_CHOICES.HAS]: "Menggunakan logo",
    [LOGO_CHOICES.NONE]: "Tanpa logo (pilihan pelanggan)",
    [LOGO_CHOICES.NOT_YET]: "Belum mempunyai logo",
  }[choice] || "Tanpa logo");

const Row = ({ label, value, testId }) => (
  <div className="grid sm:grid-cols-[170px_1fr] gap-1 sm:gap-4 py-2.5 border-b border-[#0f3f2f]/10 last:border-0">
    <dt className="t-label text-[#6b7a73]">{label}</dt>
    <dd className="text-sm text-[#14201b]" data-testid={testId}>
      {value || <span className="text-[#a9b3ae]">—</span>}
    </dd>
  </div>
);

const Block = ({ title, children }) => (
  <section className="panel p-5 sm:p-7">
    <h2 className="t-h2 text-lg sm:text-xl">{title}</h2>
    <div className="mt-4">{children}</div>
  </section>
);

const newIdempotencyKey = () =>
  (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`).replace(
    /[^A-Za-z0-9_-]/g,
    ""
  );

export default function SemakPage() {
  const { order, patch } = useOrder();
  const [submitting, setSubmitting] = useState(false);
  const [payingNow, setPayingNow] = useState(false);
  const { templateOf, productOf, loading } = useCatalog();
  const navigate = useNavigate();

  const template = templateOf(order.templateId);
  const productCode = order.productCode || "STANDARD";
  const product = productOf(productCode);
  const logo = order.logoChoice === LOGO_CHOICES.HAS ? order.logoPreview : null;

  const { quote } = useQuote({
    productCode,
    quantity: order.quantity,
    country: order.shippingAddress.country,
    state: order.shippingAddress.state,
  });

  const teruskan = async () => {
    if (!order.informationConfirmed) {
      toast.error("Sila sahkan bahawa maklumat anda adalah betul.");
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    const idempotencyKey = order.idempotencyKey || newIdempotencyKey();
    if (!order.idempotencyKey) patch({ idempotencyKey });
    const b = order.business;
    try {
      const res = await createOrder({
        idempotency_key: idempotencyKey,
        template_id: order.templateId,
        product_code: productCode,
        quantity: order.quantity,
        business_information: {
          nama_penuh: b.namaPenuh,
          jawatan: b.jawatan || null,
          nama_perniagaan: b.namaPerniagaan,
          bidang_bisnes: b.bidangBisnes || null,
          telefon: b.telefon,
          whatsapp: b.whatsapp || null,
          email: b.email || null,
          website: b.website || null,
        },
        business_address: order.businessAddress,
        shipping_address: order.shippingAddress,
        shipping_same_as_business: order.shippingSameAsBusiness,
        logo_choice: order.logoChoice,
        logo_file_reference: order.logoChoice === LOGO_CHOICES.HAS ? order.logoPath : null,
        information_confirmed: order.informationConfirmed,
        directory_listing_consent: order.directoryListingConsent,
      });
      patch({ createdOrder: res });
      window.scrollTo({ top: 0, behavior: "auto" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? "Maklumat tempahan tidak lengkap. Sila semak semula borang anda."
          : "Tempahan tidak dapat disediakan buat masa ini. Sila cuba semula."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const bayarSekarang = async () => {
    const created = order.createdOrder;
    if (!created || payingNow) return;
    setPayingNow(true);
    try {
      const res = await initPayment({
        order_reference: created.order_reference,
        access_token: created.access_token,
      });
      window.location.assign(res.payment_url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Pembayaran tidak dapat disediakan buat masa ini. Sila cuba semula."
      );
      setPayingNow(false);
    }
  };

  const hasData = order.business.namaPenuh || order.business.namaPerniagaan;

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-9 sm:py-12">
        <p className="t-label">Langkah 3 · Semak</p>
        <h1 className="mt-2 t-h1">Semak Tempahan Anda</h1>

        {order.createdOrder ? (
          <div className="mt-8 panel p-6 sm:p-9 max-w-2xl" data-testid="order-created-state">
            <span className="grid place-items-center h-12 w-12 rounded-2xl bg-gradient-to-b from-[#12876a] to-[#0f3f2f] text-white">
              <CheckCircle2 strokeWidth={1.75} className="h-6 w-6" />
            </span>
            <h2 className="mt-5 t-h2">Tempahan Anda Telah Disediakan</h2>
            <p className="mt-3 t-body text-sm">
              Semak maklumat tempahan anda sebelum meneruskan ke pembayaran.
            </p>
            <div className="mt-5 rounded-2xl border border-[#c79a3b]/30 bg-gradient-to-b from-[#fdf8ec] to-white p-5">
              <p className="t-label text-[#8a6a1f]">Nombor Tempahan</p>
              <p
                className="mt-1 font-heading text-2xl font-extrabold text-[#0f3f2f]"
                data-testid="order-reference"
              >
                {order.createdOrder.order_reference}
              </p>
              <dl className="mt-4 space-y-1.5 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-[#4a5a53]">
                    {order.createdOrder.order.product_name} — {formatQty(order.createdOrder.order.quantity)}
                  </dt>
                  <dd className="font-semibold">RM{order.createdOrder.order.product_amount}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-[#4a5a53]">Penghantaran</dt>
                  <dd className="font-semibold">
                    {order.createdOrder.order.shipping_amount === 0
                      ? "Termasuk"
                      : `RM${order.createdOrder.order.shipping_amount}`}
                  </dd>
                </div>
                <div className="flex justify-between gap-4 border-t border-[#c79a3b]/25 pt-2">
                  <dt className="font-bold">JUMLAH</dt>
                  <dd className="font-extrabold text-[#0f3f2f]" data-testid="created-total">
                    RM{order.createdOrder.order.grand_total}
                  </dd>
                </div>
              </dl>
            </div>
            <div className="mt-5 flex items-start gap-2 rounded-xl bg-[#f4f7f5] border border-[#0f3f2f]/10 p-3 t-help">
              <Info strokeWidth={1.75} className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Bayaran selamat melalui toyyibPay (FPX / kad).</span>
            </div>
            <button
              type="button"
              onClick={bayarSekarang}
              disabled={payingNow}
              data-testid="btn-bayar-sekarang"
              className="btn-primary mt-5 w-full inline-flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {payingNow && <Loader2 strokeWidth={2} className="h-4 w-4 animate-spin" />}
              {payingNow ? "Menyediakan pembayaran..." : "Bayar Sekarang"}
            </button>
            <p className="mt-4 t-help">
              Simpan nombor tempahan ini untuk rujukan anda.
            </p>
          </div>
        ) : !hasData ? (
          <div className="mt-8 panel p-8" data-testid="semak-empty">
            <p className="t-body">Anda belum mengisi maklumat kad perniagaan.</p>
            <Link to="/" data-testid="btn-mula-tempah" className="btn-primary mt-5">
              Pilih Design Sekarang
            </Link>
          </div>
        ) : (
          <div className="mt-8 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 space-y-6">
              <Block
                title={`Preview Kad — ${template ? `${template.template_id} ${template.name}` : "Design"}`}
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <p className="t-label mb-2">Depan</p>
                    <CardSurface
                      styleKey={template?.style_key || "minimalist-edge"}
                      side="front"
                      data={order}
                      logo={logo}
                      testId="semak-preview-front"
                    />
                  </div>
                  <div>
                    <p className="t-label mb-2">Belakang</p>
                    <CardSurface
                      styleKey={template?.style_key || "minimalist-edge"}
                      side="back"
                      data={order}
                      logo={logo}
                      testId="semak-preview-back"
                    />
                  </div>
                </div>
                <p className="mt-4 t-help">
                  Paparan skrin sahaja — bukan fail cetakan akhir.
                </p>
              </Block>

              <Block title="Butiran Produk">
                <dl>
                  <Row label="Jenis Kad" value={product ? `${product.name} (${product.code})` : productCode} testId="semak-product" />
                  <Row label="Material" value={product ? `${product.material} ${product.gsm}gsm` : ""} testId="semak-material" />
                  <Row label="Finishing" value={product?.finishing} testId="semak-finishing" />
                  <Row label="Saiz" value="86mm × 52mm · Landscape · Depan & Belakang" />
                  <Row label="Kuantiti" value={formatQty(order.quantity)} testId="semak-quantity" />
                </dl>
              </Block>

              <Block title="Maklumat Perniagaan">
                <dl>
                  <Row label="Nama Penuh" value={order.business.namaPenuh} testId="semak-nama-penuh" />
                  <Row label="Jawatan" value={order.business.jawatan} />
                  <Row label="Nama Perniagaan" value={order.business.namaPerniagaan} testId="semak-nama-perniagaan" />
                  <Row label="Bidang Bisnes / Moto" value={order.business.bidangBisnes} testId="semak-bidang-bisnes" />
                  <Row label="No. Telefon" value={order.business.telefon} />
                  <Row label="WhatsApp" value={order.business.whatsapp} />
                  <Row label="Email" value={order.business.email} />
                  <Row label="Website" value={order.business.website} />
                  <Row label="Status Logo" value={logoLabel(order.logoChoice)} testId="semak-logo-status" />
                </dl>
              </Block>

              <div className="grid sm:grid-cols-2 gap-6">
                <Block title="Alamat Pada Kad">
                  <address className="not-italic text-sm text-[#14201b] space-y-0.5" data-testid="semak-business-address">
                    {formatCardAddress(order.businessAddress).map((l, i) => (
                      <div key={`${l}-${i}`}>{l}</div>
                    ))}
                  </address>
                </Block>
                <Block title="Alamat Penghantaran">
                  <address className="not-italic text-sm text-[#14201b] space-y-0.5" data-testid="semak-shipping-address">
                    {formatShippingAddress(order.shippingAddress).map((l, i) => (
                      <div key={`${l}-${i}`}>{l}</div>
                    ))}
                  </address>
                </Block>
              </div>
            </div>

            <aside className="lg:col-span-5">
              <div className="lg:sticky lg:top-20 space-y-6">
                {!loading && (
                  <PriceSummary
                    productName={product?.name}
                    quantity={order.quantity}
                    productAmount={quote?.product_amount}
                    shippingAmount={quote?.shipping_amount}
                    country={order.shippingAddress.country}
                    state={order.shippingAddress.state}
                    ready={Boolean(quote)}
                  />
                )}

                <div className="panel p-5 sm:p-6 space-y-5">
                  <label className="flex gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={order.informationConfirmed}
                      onChange={(e) => patch({ informationConfirmed: e.target.checked })}
                      data-testid="checkbox-information-confirmed"
                      className="mt-0.5 h-5 w-5 shrink-0 accent-[#0f3f2f]"
                    />
                    <span className="text-sm text-[#2c3a34] leading-relaxed">
                      Saya telah menyemak dan mengesahkan bahawa semua maklumat, ejaan dan nombor
                      telefon yang diberikan adalah betul.
                    </span>
                  </label>

                  <div className="border-t border-[#0f3f2f]/10 pt-5">
                    <label className="flex gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={order.directoryListingConsent}
                        onChange={(e) => patch({ directoryListingConsent: e.target.checked })}
                        data-testid="checkbox-directory-consent"
                        className="mt-0.5 h-5 w-5 shrink-0 accent-[#c79a3b]"
                      />
                      <span className="text-sm font-semibold text-[#14201b] leading-relaxed">
                        Ya, saya mahu penyenaraian perniagaan PERCUMA di DirektoriBiz.My.
                      </span>
                    </label>
                    <p className="mt-2 ml-8 t-help leading-relaxed">
                      Maklumat dan imej kad perniagaan anda boleh digunakan untuk menyediakan
                      penyenaraian perniagaan selepas pembayaran berjaya. Pilihan ini tidak wajib.
                    </p>
                  </div>

                  <div className="flex items-start gap-2 rounded-xl bg-[#f4f7f5] border border-[#0f3f2f]/10 p-3 t-help">
                    <Info strokeWidth={1.75} className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>Tempahan akan disediakan. Pembayaran belum diproses pada fasa ini.</span>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() => navigate("/tempah")}
                      data-testid="btn-kembali-edit"
                      className="btn-secondary"
                    >
                      <ArrowLeft strokeWidth={1.75} className="h-4 w-4" />
                      Kembali &amp; Edit
                    </button>
                    <button
                      type="button"
                      onClick={teruskan}
                      disabled={submitting}
                      aria-busy={submitting}
                      data-testid="btn-teruskan"
                      className="btn-primary flex-1 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {submitting && (
                        <Loader2 strokeWidth={2} className="h-4 w-4 animate-spin" />
                      )}
                      {submitting ? "Sedang menyediakan tempahan..." : "Teruskan"}
                    </button>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
